import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useNavigate} from 'react-router'
import {listNotifications, markNotificationRead} from '../../api/notifications.js'
import {usePolling} from '../../hooks/use-polling.js'
import {AlertCard} from '../../components/alert-card/alert-card.jsx'
import styles from './notifications.module.scss'

const GROUPS = [
    {category: 'system', label: 'System'},
    {category: 'personal', label: 'Personal'},
    {category: 'issues', label: 'Issues'},
    {category: 'weather', label: 'Weather'}
]

export function Notifications() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const {data, isLoading} = usePolling({
        queryKey: ['notifications', {}],
        queryFn: () => listNotifications(),
        intervalMs: 30_000,
        staleTime: 0
    })

    const markRead = useMutation({
        mutationFn: (id) => markNotificationRead(id),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']})
    })

    const notifications = data?.notifications || []
    const byCategory = GROUPS.map(g => ({
        ...g,
        items: notifications.filter(n => n.category === g.category)
    }))

    const handleClick = (n) => {
        if (!n.read)
            markRead.mutate(n.id)
        if (n.linkPath)
            navigate(n.linkPath)
    }

    return (
        <div className={styles.screen}>
            <header className={styles.header}>
                <p className={styles.eyebrow}>Inbox</p>
                <h1 className={styles.title}>Alerts</h1>
            </header>
            {isLoading && (
                <div className={styles.skeletonGroup}>
                    {Array.from({length: 3}).map((_, i) => (
                        <div key={i} className={styles.skeleton} aria-busy="true"/>
                    ))}
                </div>
            )}
            {!isLoading && notifications.length === 0 && (
                <p className={styles.empty}>No notifications yet.</p>
            )}
            {!isLoading && byCategory.map(group => group.items.length > 0 && (
                <section key={group.category} className={styles.group}>
                    <h2 className={styles.groupTitle}>{group.label}</h2>
                    <div className={styles.items}>
                        {group.items.map(n => (
                            <AlertCard
                                key={n.id}
                                severity={n.severity}
                                title={n.title}
                                body={n.body}
                                unread={!n.read}
                                timestamp={n.createdAt}
                                onClick={() => handleClick(n)}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}
